/* ---------------------------------------------------------------------------
 * PlatformContext
 *
 * Iter 32a — Platform Root "active-tenant" context.
 *
 * Owns the tiny piece of state that lets a Platform Root operator pick
 * a tenant from the Platform Console and run normal tenant-owned
 * workflows (Scout, Pipeline scheduler, etc.) inside that tenant
 * without weakening backend tenant isolation.
 *
 * Storage:
 *   - sessionStorage key `qf_platform_active`  (NOT localStorage)
 *   - shape: { id: string, name: string, lastTouch: number }
 *   - 30-minute idle clear (read on every fetch via the wrapper in
 *     AuthContext.js)
 *
 * Visibility:
 *   - The provider is mounted unconditionally, but its banner + setters
 *     are only meaningful when `auth.user.is_platform_root === true`.
 *   - Tenant users will never see UI affected by this state.
 * ---------------------------------------------------------------------------
 */
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

const KEY = 'qf_platform_active';
const IDLE_MS = 30 * 60 * 1000; // 30 min

const PlatformContext = createContext({
  activeTenantId: null,
  activeTenantName: null,
  setActiveTenant: () => {},
  clearActiveTenant: () => {},
});

function _read() {
  try {
    if (typeof sessionStorage === 'undefined') return null;
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj || !obj.id) return null;
    if (Date.now() - Number(obj.lastTouch || 0) > IDLE_MS) {
      sessionStorage.removeItem(KEY);
      return null;
    }
    return obj;
  } catch {
    return null;
  }
}

function _write(obj) {
  try {
    if (typeof sessionStorage === 'undefined') return;
    sessionStorage.setItem(KEY, JSON.stringify(obj));
  } catch { /* storage quota / private mode — ignore */ }
}

function _clear() {
  try {
    if (typeof sessionStorage === 'undefined') return;
    sessionStorage.removeItem(KEY);
  } catch { /* ignore */ }
}

export function PlatformProvider({ children }) {
  const [active, setActive] = useState(_read);

  // Re-hydrate every minute so the banner accurately reflects an idle
  // expiry that happened during a quiet stretch (no fetches).
  useEffect(() => {
    const t = setInterval(() => {
      const fresh = _read();
      setActive((curr) => {
        const a = curr ? curr.id : null;
        const b = fresh ? fresh.id : null;
        return a === b ? curr : fresh;
      });
    }, 60 * 1000);
    return () => clearInterval(t);
  }, []);

  // P0 demo-safety: when the fetch wrapper self-heals a stale active
  // tenant (backend returned `400 active_tenant_invalid` after a redeploy
  // / reseed) it dispatches `quod:active-tenant-cleared`. Drop the
  // banner immediately so the UI matches reality and the operator sees
  // a clean Platform Console state instead of a stale "Acting as ..."
  // banner pointing at a tenant that no longer exists.
  useEffect(() => {
    const onCleared = () => setActive(null);
    if (typeof window !== 'undefined') {
      window.addEventListener('quod:active-tenant-cleared', onCleared);
      return () => window.removeEventListener('quod:active-tenant-cleared', onCleared);
    }
    return undefined;
  }, []);

  const setActiveTenant = useCallback((tenant) => {
    if (!tenant || !tenant.id) return;
    const obj = {
      id: String(tenant.id),
      name: String(tenant.name || tenant.slug || tenant.id),
      lastTouch: Date.now(),
    };
    _write(obj);
    setActive(obj);
  }, []);

  const clearActiveTenant = useCallback(() => {
    _clear();
    setActive(null);
  }, []);

  return (
    <PlatformContext.Provider
      value={{
        activeTenantId: active ? active.id : null,
        activeTenantName: active ? active.name : null,
        setActiveTenant,
        clearActiveTenant,
      }}
    >
      {children}
    </PlatformContext.Provider>
  );
}

export function usePlatform() {
  return useContext(PlatformContext);
}

export default PlatformContext;
